/**
 * Persistence Middleware for Zustand Stores
 *
 * Adapts Zustand's persist middleware to work with the existing asyncStorage service.
 * This ensures backward compatibility with the current storage implementation while
 * enabling Zustand store persistence.
 *
 * Enhanced with encryption support for sensitive data (OWASP A02 mitigation).
 *
 * @module store/middleware/persistenceMiddleware
 */

import { asyncStorage } from '../../shared/services/storage/asyncStorageService';
import { secureStorage } from '../../shared/utils/secureStorage';
import logger from '../../shared/services/logging/logger';

/**
 * Storage adapter that bridges Zustand persist with asyncStorage
 *
 * Provides async storage interface compatible with Zustand's persist middleware.
 * Handles read/write operations with proper error logging.
 */
export const asyncStorageAdapter = {
  /**
   * Retrieve value from asyncStorage
   * @param {string} name - Storage key
   * @returns {Promise<string|null>} Stored value or null
   */
  getItem: async (name) => {
    try {
      const value = await asyncStorage.get(name);
      if (value === undefined || value === null) {
        return null;
      }
      // Zustand expects string values, asyncStorage returns parsed values
      return typeof value === 'string' ? value : JSON.stringify(value);
    } catch (error) {
      logger.error('Error reading from asyncStorage', {
        key: name,
        error: error.message
      });
      return null;
    }
  },

  /**
   * Store value in asyncStorage
   * @param {string} name - Storage key
   * @param {string} value - Value to store (JSON string)
   * @returns {Promise<void>}
   */
  setItem: async (name, value) => {
    try {
      // Use setSync so the value lands in localStorage immediately — a debounced
      // write risks losing the update if the user refreshes before the flush fires.
      asyncStorage.setSync(name, value);
    } catch (error) {
      logger.error('Error writing to asyncStorage', {
        key: name,
        error: error.message
      });
    }
  },

  /**
   * Remove value from asyncStorage
   * @param {string} name - Storage key
   * @returns {Promise<void>}
   */
  removeItem: async (name) => {
    try {
      await asyncStorage.remove(name);
    } catch (error) {
      logger.error('Error removing from asyncStorage', {
        key: name,
        error: error.message
      });
    }
  }
};

/**
 * Encrypted storage adapter for sensitive data
 * Uses AES-256 encryption via secureStorage utility
 *
 * IMPORTANT: Only use for sensitive data (userData, tokens, roles)
 * Do NOT use for non-sensitive data (performance overhead)
 */
export const encryptedStorageAdapter = {
  /**
   * Retrieve and decrypt value from storage.
   * Prefers sessionStorage (tab-isolated) to prevent cross-tab session bleed,
   * falls back to localStorage for fresh tabs with no session data yet.
   * @param {string} name - Storage key
   * @returns {Promise<string|null>} Decrypted value or null
   */
  getItem: async (name) => {
    try {
      // Prefer sessionStorage — tab-isolated, so duplicating a tab or having
      // a second user logged in on another tab won't bleed into this tab.
      const sessionValue = await secureStorage.getEncryptedSession(name);
      if (sessionValue !== null && sessionValue !== undefined) {
        return typeof sessionValue === 'string' ? sessionValue : JSON.stringify(sessionValue);
      }
      // Fall back to localStorage for fresh tabs that have no session data yet.
      const value = await secureStorage.getEncrypted(name);
      if (value === null || value === undefined) {
        return null;
      }
      // Migrate into sessionStorage so this tab is now isolated going forward.
      await secureStorage.setEncryptedSession(name, value);
      return typeof value === 'string' ? value : JSON.stringify(value);
    } catch (error) {
      logger.error('Error reading from encrypted storage', {
        key: name,
        error: error.message
      });
      return null;
    }
  },

  /**
   * Encrypt and store value.
   * Writes to sessionStorage (tab-isolated) AND localStorage (cross-tab persistence).
   * @param {string} name - Storage key
   * @param {string} value - Value to encrypt and store (JSON string)
   * @returns {Promise<void>}
   */
  setItem: async (name, value) => {
    try {
      const parsedValue = typeof value === 'string' ? JSON.parse(value) : value;
      // Write to sessionStorage first for immediate tab isolation.
      await secureStorage.setEncryptedSession(name, parsedValue);
      // Also write to localStorage so fresh tabs can bootstrap from it.
      await secureStorage.setEncryptedSync(name, parsedValue);
    } catch (error) {
      logger.error('Error writing to encrypted storage', {
        key: name,
        error: error.message
      });
    }
  },

  /**
   * Remove encrypted value from both storages.
   * @param {string} name - Storage key
   * @returns {Promise<void>}
   */
  removeItem: async (name) => {
    try {
      secureStorage.removeEncryptedSession(name);
      secureStorage.removeEncrypted(name);
    } catch (error) {
      logger.error('Error removing from encrypted storage', {
        key: name,
        error: error.message
      });
    }
  }
};

/**
 * Custom persist options factory
 * Creates consistent persist configuration for Zustand stores
 *
 * @param {string} name - Storage key name
 * @param {Object} options - Additional options
 * @param {Function} options.partialize - Function to select which state to persist
 * @param {Function} options.onRehydrateStorage - Callback after rehydration
 * @param {boolean} options.encrypted - Whether to use encrypted storage (default: false)
 * @returns {Object} Persist configuration
 */
export const createPersistConfig = (name, options = {}) => {
  // Use encrypted storage for sensitive data
  const storageAdapter = options.encrypted ? encryptedStorageAdapter : asyncStorageAdapter;

  return {
    name,
    storage: storageAdapter,
    version: 1,
    partialize: options.partialize || ((state) => state),
    onRehydrateStorage: () => (state, error) => {
      if (error) {
        logger.error('Store rehydration failed', {
          storeName: name,
          encrypted: options.encrypted || false,
          error: error.message
        });
      } else {
        logger.debug('Store rehydrated successfully', {
          storeName: name,
          encrypted: options.encrypted || false
        });
      }

      if (options.onRehydrateStorage) {
        options.onRehydrateStorage(state, error);
      }
    }
  };
};
